import { useRef, useState } from 'react';
import { format, formatDistanceStrict, isValid } from 'date-fns';

import {
  Anchor,
  Box,
  Card,
  Heading,
  Paragraph,
  Popover,
  Text,
  TextButton,
  TriggerText,
} from '@zillow/constellation-10';

import { LoadingParagraph, LoadingTag, LoadingText } from '@zg-rentals/loading-components';
import {
  useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useScreenSize } from '../../hooks/useScreenSize';
import { useSyndicationUpdates } from '../../hooks/useSyndicationUpdates';
import accessUtils from '../../utils/accessUtils';
import numberUtils from '../../utils/numberUtils';
import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';
import stringHelper from '../../utils/stringHelper';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';
import { LISTING_SUMMARY_DISPLAY_STATUS } from '../../constants/ListingSummaryConstants';
import TimeConstants from '../../constants/TimeConstants';
import { RELISTING_STEP_SUFFIX, STEP_DELIMITER, STEP_KEYS } from '../CreateListingPage/constants/stepKeys';

import DeactivateListingModal from '../modals/DeactivateListingModal';
import FeedListingDeactivateModal from '../modals/FeedListingDeactivateModal';
import Alert from './components/Alert';
import Comps from './components/Comps';
import CTA from './components/CTA';
import FeaturesOfPremium from './components/FeaturesOfPremium';
import PremiumStatistics from './components/PremiumStatistics';
import PremiumUpgrade from './components/PremiumUpgrade';
import Statistics from './components/Statistics';

import { containerStyles, detailContainerStyles, verticalLineStyles } from './styles';

import { useAuth } from '../AuthProvider';
import ConnectedModuleCard from '../ConnectedModuleCard';
import { WrongAccountAlert } from '../Dashboard/WrongAccountAlert';
import ListingCompletenessScore from '../ListingCompletenessScore';
import ListingAvailability from '../OverviewPage/modules/Listing/ListingAvailability';
import { ListingModuleHeading } from '../OverviewPage/modules/Listing/ListingModuleHeading';
import NetworkView, { DISPLAY_TYPE } from '../OverviewPage/modules/Listing/NetworkView';
import PremiumCTA from '../OverviewPage/modules/Listing/PremiumCTA';
import PremiumPaymentGateway from '../PaymentGateway/PremiumPaymentGateway';
import ProgressStepperSummary from '../ProgressStepperSummary';

const activeForLink =
  'https://www.zillow.help/article/how-is-the-days-listed-counter-determined-zd30237392565907' as const;
const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/' as const;

type Props = {
  listingAlias: string;
};

const ListingPage = ({ listingAlias }: Props) => {
  const { user } = useAuth();
  const {
    data,
    isPending,
    error: listingSummaryError,
  } = useRentalManagerUserPropertySummaryWebApiHandler_listing(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: TimeConstants.SECONDS_10 },
  );

  const {
    data: overviewData,
    isPending: isOverviewPending,
    isError: isOverviewError,
    error: overviewError,
  } = useRentalManagerUserPropertyDetailsWebApiHandler_overview({ listingAlias }, { enabled: Boolean(listingAlias) });

  const isActive = data?.isActive ?? false;
  const isFeed = data?.isFeed ?? false;
  const displayStatus = data?.syndication?.overview?.displayStatus?.toLowerCase() ?? '';
  const status = displayStatus as keyof typeof LISTING_SUMMARY_DISPLAY_STATUS;
  const expirationMs = data?.expirationMs;
  const rentZestimate = data?.rentZestimate ?? 0;
  const lastActivatedMs = data?.lastActivatedMs;
  const isInactive = displayStatus === LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE;
  const hasAnalyticsRentComps = data?.hasAnalyticsRentComps ?? false;
  const { isScreenMobile } = useScreenSize();

  // Clean up relisting step suffix before comparing
  let { step } = data?.modularListingProgress || {};
  if (step === `${STEP_KEYS.REVIEW}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`.toLowerCase()) {
    step = step.split(STEP_DELIMITER)[0];
  }
  const canResumeModularListingCreation =
    step !== undefined &&
    accessUtils.canAccessModularListing({
      isMultifamily: overviewData?.isMultifamily ?? false,
      listingTypeCode: overviewData?.listingType?.code ?? 0,
      lastActivatedMs,
      userCanAccessModularListing: user?.features?.canAccessModularListing ?? false,
    });

  const shouldCheckIn = overviewData?.listingAvailability?.shouldCheckIn ?? false;
  const freemium = overviewData?.freemium;
  const isFreemiumEnabled = freemium?.isEnabled ?? false;
  const activeProductType = freemium?.activeProduct?.type;
  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const isActiveProductTypeFree = activeProductType === PRODUCT_TYPE_NAME_TO_ID.FREE;
  const activeProductExpirationTimestamp = freemium?.activeProduct?.expirationTimestamp;
  const includePremiumBorderTop = isFreemiumEnabled && isActiveProductTypePremium;
  const hideBorderOnMobile = !(includePremiumBorderTop && !isScreenMobile);

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: isFreemiumEnabled,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingPage' } },
    });

  const featuresOfPremiumRef = useRef<HTMLDivElement>(null);

  const [displayDeactivateModal, setDisplayDeactivateModal] = useState(false);
  const [displayFeedDeactivateModal, setDisplayFeedDeactivateModal] = useState(false);
  const [displayPremiumPaymentGateway, setDisplayPremiumPaymentGateway] = useState(false);

  const handleUnlockClick = () => {
    const element = featuresOfPremiumRef.current;
    if (element) {
      window.scrollTo({ top: element.offsetTop - 100, behavior: 'smooth' });
    }
  };

  useSyndicationUpdates({ listingAlias });

  if (listingSummaryError || isOverviewError) {
    if (
      (listingSummaryError && (listingSummaryError as DatarouterError).responseStatus === 403) ||
      (overviewError && (overviewError as DatarouterError).responseStatus === 403)
    ) {
      return <WrongAccountAlert />;
    }

    return (
      <ConnectedModuleCard title="Listing" isLoading={false}>
        <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {DatarouterError.getDisplayMessage(listingSummaryError)}
        </Box>
      </ConnectedModuleCard>
    );
  }

  const displayPremiumCTA =
    (isFreemiumEnabled && isActive) ||
    (accessUtils.canAccessTopOfFunnelPremium({
      isFreemiumEnabled,
      isActiveProductTypePremium,
      isTopOfFunnelEnrollmentEligible,
    }) &&
      !isActiveProductTypeFree);

  const displayFeaturesOfPremium = isFreemiumEnabled && (isActive || isActiveProductTypePremium);

  const listedFor =
    lastActivatedMs && displayStatus !== LISTING_SUMMARY_DISPLAY_STATUS.DECLINED
      ? formatDistanceStrict(new Date(), new Date(lastActivatedMs))
      : '-';

  const lastListed =
    lastActivatedMs && isValid(new Date(lastActivatedMs)) ? format(new Date(lastActivatedMs), 'MMM yyyy') : '–';

  const renderActiveDetails = () => (
    <Box css={detailContainerStyles}>
      <LoadingParagraph
        textStyle="body-sm"
        isLoading={isPending}
        loadingWidth="40%"
        css={{ marginBottom: '100', whiteSpace: 'nowrap' }}
      >
        Active for{' '}
        <Popover
          header={<Heading level={6}>Calculating your active time</Heading>}
          body={
            <>
              <Paragraph css={{ marginBottom: '400' }}>
                This counter shows the amount of time since you last activated your listing. If you deactivate and then
                reactivate within 31 days, your public listing will show the cumulative time it has been active.
              </Paragraph>
              <Popover.Close>
                <TextButton asChild>
                  <a target="_blank" href={activeForLink} rel="noopener noreferrer">
                    Learn more
                  </a>
                </TextButton>
              </Popover.Close>
            </>
          }
        />
      </LoadingParagraph>
      <LoadingParagraph
        textStyle="body-bold"
        isLoading={isPending}
        loadingWidth="100%"
        css={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
      >
        {listedFor}
      </LoadingParagraph>
    </Box>
  );

  const renderInactiveDetails = () => (
    <>
      <Box css={detailContainerStyles}>
        <Text
          textStyle="body-sm"
          asChild
          css={{ marginBottom: '100', color: 'text.neutral', textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          <p>Last listed</p>
        </Text>
        <Text
          textStyle="body-bold"
          asChild
          css={{ color: 'text.neutral', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
        >
          <p>{lastListed}</p>
        </Text>
      </Box>
      {rentZestimate !== 0 && (
        <>
          <Box css={verticalLineStyles} data-testid="StyledVerticalLine" />
          <Box css={detailContainerStyles}>
            <Text
              textStyle="body-sm"
              asChild
              css={{
                marginBottom: '100',
                color: 'text.subtle',
                whiteSpace: { base: 'normal', md: 'nowrap' },
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              <p>Rent Zestimate®</p>
            </Text>
            <Text
              textStyle="body-bold"
              asChild
              css={{ color: 'text.subtle', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
              <p>
                <Popover
                  header={<Heading level={6}>Rent Zestimate®</Heading>}
                  body={
                    <>
                      The Rent Zestimate is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
                      <Anchor
                        css={{ textStyle: 'body' }}
                        target="_blank"
                        href={rentZestimateLink}
                        rel="noopener noreferrer"
                      >
                        Learn more
                      </Anchor>
                      {' about the Rent Zestimate.'}
                    </>
                  }
                  trigger={<TriggerText>{numberUtils.asCurrency(rentZestimate)}</TriggerText>}
                />
              </p>
            </Text>
          </Box>
        </>
      )}
    </>
  );

  const renderByStatus = () => {
    if (isPending || isOverviewPending) {
      return (
        <LoadingText isLoading={true} loadingWidth="100%">
          &nbsp;
        </LoadingText>
      );
    }

    switch (displayStatus) {
      case LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE: {
        if (canResumeModularListingCreation) {
          return <ProgressStepperSummary status={status} step={step ?? ''} />;
        }
        return (
          <Comps
            listingAlias={listingAlias}
            address={overviewData?.address}
            hasAnalyticsRentComps={hasAnalyticsRentComps}
          />
        );
      }
      case LISTING_SUMMARY_DISPLAY_STATUS.PARTIALLY_PUBLISHED:
      case LISTING_SUMMARY_DISPLAY_STATUS.DECLINED:
        return <Alert universal={data?.syndication?.universal} displayStatus={displayStatus} />;
      case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHED:
      case LISTING_SUMMARY_DISPLAY_STATUS.PUBLISHING:
        if (isFreemiumEnabled && isActiveProductTypePremium) {
          return <PremiumStatistics listingAlias={listingAlias} />;
        }
        return (
          <Statistics
            listingAlias={listingAlias}
            address={overviewData?.address}
            hasAnalyticsRentComps={hasAnalyticsRentComps}
          />
        );
      default:
        return null;
    }
  };

  const renderHeading = () => {
    if (isFreemiumEnabled && isActiveProductTypePremium) return 'Premium listing';
    if (isFreemiumEnabled && isActive && isActiveProductTypeFree) return 'Basic listing';
    return 'Listing';
  };

  return (
    <div data-testid="ConnectedListing">
      <ConnectedModuleCard
        titleGrow={0}
        hideBorderOnMobile={hideBorderOnMobile}
        includeBorderTop={includePremiumBorderTop}
        justifyHeadingContent={includePremiumBorderTop ? 'space-between' : undefined}
      >
        {/* Heading: [PremiumBadge + title + status tag] | [NetworkView] */}
        <Box
          css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' }, justifyContent: 'space-between' }}
          data-testid="ConnectedListing-Heading"
        >
          <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <ListingModuleHeading isPremiumListing={includePremiumBorderTop} headingText={renderHeading()} />
            <LoadingTag
              tone={isPending ? 'gray' : getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, status)}
              size="sm"
              isLoading={isPending}
              loadingWidth="7rem"
              css={{ marginLeft: '200' }}
            >
              {stringHelper.sentenceCase(status)}
            </LoadingTag>
          </Box>

          <Box css={{ marginBottom: { base: '400', lg: 0 } }} />

          {displayStatus !== LISTING_SUMMARY_DISPLAY_STATUS.INACTIVE &&
            displayStatus !== LISTING_SUMMARY_DISPLAY_STATUS.DECLINED &&
            !isOverviewPending && (
              <NetworkView
                label="View your listing"
                size="sm"
                status={data?.syndication?.overview?.status}
                syndication={data?.syndication}
                displayType={DISPLAY_TYPE.DROPDOWN_BUTTON}
                isLoading={isPending}
                isMultifamily={overviewData?.isMultifamily ?? false}
                useFixedWidth={true}
              />
            )}
        </Box>

        <Box css={{ marginBottom: { base: '400', lg: 'md' } }} />

        <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'inherit' }}>
          {/* Listing availability check-in banner */}
          {expirationMs && shouldCheckIn && (
            <Box css={{ marginBottom: '600' }}>
              <ListingAvailability listingAlias={listingAlias} expirationMs={expirationMs} />
            </Box>
          )}

          {/* Main content grid */}
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0,1fr))', gap: '400' }}>
            {/* Left column: details + status-specific content */}
            <Box
              data-testid="Grid-DetailsContainer"
              css={{ gridColumnEnd: { base: 'span 12', xl: !canResumeModularListingCreation ? 'span 7' : 'span 12' } }}
            >
              <Card tone="neutral" elevated={false} css={{ minHeight: '200px' }}>
                <Box css={{ ...containerStyles, marginBottom: '0' }} data-testid="Details-Full">
                  {isInactive ? renderInactiveDetails() : renderActiveDetails()}
                </Box>
                <Box
                  css={{
                    flexGrow: 0,
                    marginBottom: '15px',
                    borderBottom: '1px solid {colors.border.muted}',
                    borderRight: 'none',
                  }}
                />
                <Box css={{ minHeight: '60px' }}>{renderByStatus()}</Box>

                {canResumeModularListingCreation && (
                  <Box css={{ marginBlock: '400', display: 'flex', justifyContent: 'center' }}>
                    <CTA
                      listingAlias={listingAlias}
                      isActive={isActive}
                      canDeactivate={data?.canDeactivate ?? false}
                      canResumeModularListingCreation={canResumeModularListingCreation}
                      status={displayStatus}
                      lastActivatedMs={lastActivatedMs}
                      isUpdateActiveStatusAllowed={overviewData?.updateActiveStatus?.isAllowed ?? true}
                      partialMessages={overviewData?.updateActiveStatus?.partialMessages ?? []}
                      isLoading={isPending}
                      onDeactivateClick={() =>
                        isFeed ? setDisplayFeedDeactivateModal(true) : setDisplayDeactivateModal(true)
                      }
                    />
                  </Box>
                )}

                {displayPremiumCTA && !isActiveProductTypePremium && (
                  <PremiumCTA
                    listingAlias={listingAlias}
                    isLoading={isPending}
                    isActiveProductTypeFree={isActiveProductTypeFree}
                    onUnlockClick={handleUnlockClick}
                  />
                )}
              </Card>
            </Box>

            {/* Right column: ListingCompletenessScore + CTA + PremiumUpgrade */}
            {!canResumeModularListingCreation && (
              <Box
                data-testid="Grid-ListingCompletenessScoreContainer"
                css={{ gridColumnEnd: { base: 'span 12', xl: 'span 5' } }}
              >
                <Card tone="neutral" elevated={false} css={{ minHeight: '200px' }}>
                  <ListingCompletenessScore
                    isFromDashboard={true}
                    canResumeModularListingCreation={canResumeModularListingCreation}
                  />
                  <Box css={{ marginTop: '400', marginBottom: '600' }}>
                    <CTA
                      listingAlias={listingAlias}
                      isActive={isActive}
                      canDeactivate={data?.canDeactivate ?? false}
                      canResumeModularListingCreation={canResumeModularListingCreation}
                      status={displayStatus}
                      lastActivatedMs={lastActivatedMs}
                      isUpdateActiveStatusAllowed={overviewData?.updateActiveStatus?.isAllowed ?? true}
                      partialMessages={overviewData?.updateActiveStatus?.partialMessages ?? []}
                      isLoading={isPending}
                      onDeactivateClick={() =>
                        isFeed ? setDisplayFeedDeactivateModal(true) : setDisplayDeactivateModal(true)
                      }
                    />
                  </Box>
                  {!isOverviewError && isActiveProductTypeFree && isFreemiumEnabled && isActive && (
                    <PremiumUpgrade onUpgradeClick={handleUnlockClick} />
                  )}
                </Card>
              </Box>
            )}
          </Box>

          {/* PremiumCTA — analytics link + expiration, shown outside grid for premium users only */}
          {displayPremiumCTA && isActiveProductTypePremium && (
            <PremiumCTA
              listingAlias={listingAlias}
              isLoading={isPending}
              isActiveProductTypeFree={isActiveProductTypeFree}
              activeProductExpirationTimestamp={activeProductExpirationTimestamp}
            />
          )}
        </Box>
      </ConnectedModuleCard>

      {displayFeaturesOfPremium && (
        <Box ref={featuresOfPremiumRef} css={{ marginTop: { base: '1200', xl: '1000' } }}>
          <FeaturesOfPremium
            listingAlias={listingAlias}
            isActive={isActive}
            isActiveProductTypeFree={isActiveProductTypeFree}
            isActiveProductTypePremium={isActiveProductTypePremium}
            onUpgradeClick={() => setDisplayPremiumPaymentGateway(true)}
          />
        </Box>
      )}

      <DeactivateListingModal
        listingAlias={listingAlias}
        open={displayDeactivateModal}
        onOpenChange={setDisplayDeactivateModal}
      />
      <FeedListingDeactivateModal
        listingAlias={listingAlias}
        open={displayFeedDeactivateModal}
        onOpenChange={setDisplayFeedDeactivateModal}
      />
      {displayPremiumPaymentGateway && (
        <PremiumPaymentGateway
          listingAlias={listingAlias}
          isActive={isActive}
          freemiumData={freemium ?? undefined}
          isPremiumMidfunnelPayment={true}
          isInBuildingGroup={overviewData?.freemium?.isInBuildingGroup ?? false}
          onClose={() => setDisplayPremiumPaymentGateway(false)}
        />
      )}
    </div>
  );
};

export default ListingPage;
